import React, { useEffect, useState } from 'react';
import { LmsAnalytics } from './lms-analytics.component';
import { LmsAnalyticsData } from './lms-analytics.data';
import { adaptApiDataToDashboard, EnrollmentApiData } from './lms-analytics.adapter';
import { SkeletonLoader } from '../../_components/skeleton-loader/SkeletonLoader';
import Config from '../../_config/config';

interface LmsAnalyticsApiProps {
  apiUrl?: string;
}

/**
 * Wrapper component that fetches data from the API and renders the LmsAnalytics dashboard
 * Default API URL: http://localhost:4000/api/v1/common/lms-admin-dashboard (nodefork)
 * Uses nodefork API with analytics_dashboard filter to show only selected programs
 */
export const LmsAnalyticsApi: React.FC<LmsAnalyticsApiProps> = ({
  apiUrl = `${Config.nodeSiteUrl}v1/common/lms-admin-dashboard`
}) => {
  const [data, setData] = useState<LmsAnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get authentication token from localStorage
        const getAuthHeaders = (): HeadersInit => {
          const headers: HeadersInit = {
            'Content-Type': 'application/json',
          };

          const currentUser = localStorage.getItem('currentUser');
          if (currentUser && currentUser !== 'undefined') {
            try {
              const user = JSON.parse(currentUser);
              const token = user.token;
              if (token) {
                headers['Authorization'] = `Bearer ${token}`;
              }
            } catch (error) {
              console.error('Failed to parse currentUser from localStorage:', error);
            }
          }

          return headers;
        };

        const authHeaders = getAuthHeaders();

        // Fetch enrollments, lesson plans, baseline and endline topic analysis
        const [enrollmentsResponse, lessonPlansResponse, baselineResponse, endlineResponse] = await Promise.all([
          fetch(apiUrl, { headers: authHeaders }),
          fetch(`${Config.nodeSiteUrl}v1/common/lesson-plans/active`, { headers: authHeaders }),
          fetch(`${Config.nodeSiteUrl}v1/analytics/learner-topic-analysis/testcategory/1001`, { headers: authHeaders }),
          fetch(`${Config.nodeSiteUrl}v1/analytics/learner-topic-analysis/testcategory/1002`, { headers: authHeaders })
        ]);

        if (!enrollmentsResponse.ok) {
          throw new Error(`Enrollments API error: ${enrollmentsResponse.status} - ${apiUrl}`);
        }
        if (!lessonPlansResponse.ok) {
          throw new Error(`Lesson Plans API error: ${lessonPlansResponse.status} - ${Config.nodeSiteUrl}v1/common/lesson-plans/active`);
        }

        let enrollmentsData: EnrollmentApiData[];
        let lessonPlansData: any[];

        try {
          enrollmentsData = await enrollmentsResponse.json();
        } catch (err) {
          throw new Error(`Failed to parse Enrollments API response from ${apiUrl}: ${err instanceof Error ? err.message : 'Invalid JSON'}`);
        }

        try {
          lessonPlansData = await lessonPlansResponse.json();
        } catch (err) {
          throw new Error(`Failed to parse Lesson Plans API response from ${Config.nodeSiteUrl}v1/common/lesson-plans/active: ${err instanceof Error ? err.message : 'Invalid JSON'}`);
        }

        // Baseline and endline are optional - if not available, use empty arrays
        let baselineData: any[] = [];
        let endlineData: any[] = [];

        if (baselineResponse.ok) {
          try {
            baselineData = await baselineResponse.json();
          } catch (err) {
            console.warn('Failed to parse Baseline API response, using empty data');
          }
        } else {
          console.warn(`Baseline Topic Analysis API not available (${baselineResponse.status})`);
        }

        if (endlineResponse.ok) {
          try {
            endlineData = await endlineResponse.json();
          } catch (err) {
            console.warn('Failed to parse Endline API response, using empty data');
          }
        } else {
          console.warn(`Endline Topic Analysis API not available (${endlineResponse.status})`);
        }

        // Extract competency IDs (tagIds) from lesson plans
        const competencyIds = new Set<number>();
        try {
          lessonPlansData.forEach((lp: any) => {
            if (lp.info?.competencyWeights) {
              Object.keys(lp.info.competencyWeights).forEach(id => {
                competencyIds.add(parseInt(id));
              });
            }
          });
        } catch (err) {
          console.warn('Error extracting competency IDs from lesson plans:', err);
        }

        // Fetch tag names for competencies
        let tagsData: any[] = [];
        if (competencyIds.size > 0) {
          try {
            const tagsResponse = await fetch(
              `${Config.nodeSiteUrl}v1/common/tags/by-ids`,
              {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ tagIds: Array.from(competencyIds) })
              }
            );
            if (tagsResponse.ok) {
              tagsData = await tagsResponse.json();
            } else {
              console.warn(`Tags API returned ${tagsResponse.status}, continuing without tag names`);
            }
          } catch (err) {
            console.warn('Error fetching tags:', err);
          }
        }

        try {
          const dashboardData = adaptApiDataToDashboard(enrollmentsData, lessonPlansData, tagsData, baselineData, endlineData);
          setData(dashboardData);
        } catch (err) {
          throw new Error(`Failed to process dashboard data: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
        console.error('Error fetching LMS dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  if (loading) {
    return <SkeletonLoader type="dashboard" />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: 'var(--status-danger)', marginBottom: '16px' }}>Error: {error}</div>
        <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>
          Check browser console for detailed error information.
        </div>
        <details style={{ marginTop: '16px', textAlign: 'left', maxWidth: '600px', margin: '16px auto' }}>
          <summary style={{ cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)' }}>API Endpoints Being Used</summary>
          <ul style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.8' }}>
            <li>Enrollments: {apiUrl}</li>
            <li>Lesson Plans: {Config.nodeSiteUrl}v1/common/lesson-plans/active</li>
            <li>Baseline Analysis: {Config.nodeSiteUrl}v1/analytics/learner-topic-analysis/testcategory/1001 (optional)</li>
            <li>Endline Analysis: {Config.nodeSiteUrl}v1/analytics/learner-topic-analysis/testcategory/1002 (optional)</li>
            <li>Tags: {Config.nodeSiteUrl}v1/common/tags/by-ids (POST, optional)</li>
          </ul>
        </details>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: 'var(--text-muted)' }}>No data available</div>
      </div>
    );
  }

  // Check for ?debug in URL to show data source badges
  const showDebug = typeof window !== 'undefined' && window.location.search.includes('debug');

  return <LmsAnalytics data={data} showDataSource={showDebug} />;
};

export default LmsAnalyticsApi;
